import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { loyaltyAPI, getApiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";

type Props = {
	customerId: number;
	rewardPoints: number;
	onPointsUpdated?: () => void;
};

export function CustomerLoyaltyPanel({ customerId, rewardPoints, onPointsUpdated }: Props) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [pointsBalance, setPointsBalance] = useState(rewardPoints);
	const [entries, setEntries] = useState<
		Array<{ id: number; type: string; points: number; createdAt: string; notes: string | null }>
	>([]);
	const [adjustOpen, setAdjustOpen] = useState(false);
	const [adjustPoints, setAdjustPoints] = useState("");
	const [adjustNotes, setAdjustNotes] = useState("");
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setPointsBalance(rewardPoints);
	}, [rewardPoints]);

	const load = useCallback(async () => {
		try {
			const data = await loyaltyAPI.getLedger(customerId, { page: 1, limit: 10 });
			setEntries(data.entries ?? []);
		} catch {
			setEntries([]);
		}
	}, [customerId]);

	useEffect(() => {
		load();
	}, [load]);

	const ledgerTypeLabel = (type: string) => {
		const key = `checkoutPromotions.loyalty.ledgerTypes.${type}`;
		const translated = t(key);
		return translated === key ? type : translated;
	};

	const handleAdjust = async () => {
		const points = parseInt(adjustPoints, 10);
		if (!Number.isFinite(points) || points === 0) return;
		try {
			setSaving(true);
			const result = await loyaltyAPI.adjust(customerId, { points, notes: adjustNotes || undefined });
			if (typeof result.balance === "number" && Number.isFinite(result.balance)) {
				setPointsBalance(result.balance);
			}
			toast({ title: t("common.success"), description: t("checkoutPromotions.loyalty.adjusted") });
			setAdjustOpen(false);
			setAdjustPoints("");
			setAdjustNotes("");
			await load();
			onPointsUpdated?.();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.loyalty.errors.save")),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between gap-4">
				<div className="flex flex-col gap-2">
					<CardTitle>{t("checkoutPromotions.loyalty.customerTitle")}</CardTitle>
					<CardDescription>{t("checkoutPromotions.loyalty.customerDescription")}</CardDescription>
				</div>
				<PermissionGate permission="cashierLoyalty.update">
					<Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
						<DialogTrigger asChild>
							<Button size="sm" variant="outline">
								{t("checkoutPromotions.loyalty.adjust")}
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>{t("checkoutPromotions.loyalty.adjust")}</DialogTitle>
							</DialogHeader>
							<div className="space-y-4 py-2">
								<div className="space-y-2">
									<Label>{t("checkoutPromotions.loyalty.pointsDelta")}</Label>
									<Input
										aria-label={t("checkoutPromotions.loyalty.pointsDelta")}
										type="number"
										value={adjustPoints}
										onChange={e => setAdjustPoints(e.target.value)}
										placeholder={t("common.adjustPlaceholder")}
									/>
								</div>
								<div className="space-y-2">
									<Label>{t("common.notes")}</Label>
									<Input
										aria-label={t("common.notes")}
										value={adjustNotes}
										onChange={e => setAdjustNotes(e.target.value)}
									/>
								</div>
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={() => setAdjustOpen(false)}>
									{t("common.cancel")}
								</Button>
								<Button onClick={handleAdjust} disabled={saving || !adjustPoints}>
									{t("common.save")}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</PermissionGate>
			</CardHeader>
			<CardContent className="space-y-4">
				<p className="text-2xl font-semibold">
					{pointsBalance}{" "}
					<span className="text-sm text-muted-foreground font-normal">
						{t("checkoutPromotions.loyalty.points")}
					</span>
				</p>
				{entries.length > 0 && (
					<div className="record-surface">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("common.date")}</TableHead>
									<TableHead>{t("checkoutPromotions.coupons.type")}</TableHead>
									<TableHead>{t("checkoutPromotions.loyalty.points")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{entries.map(entry => (
									<TableRow key={entry.id}>
										<TableCell>{formatDate(entry.createdAt)}</TableCell>
										<TableCell>
											<Badge variant="outline">{ledgerTypeLabel(entry.type)}</Badge>
										</TableCell>
										<TableCell>{entry.points > 0 ? `+${entry.points}` : entry.points}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
